import React, { useContext, useEffect, useRef, useState } from "react";
import cx from "classnames";

import global from "../../scss/dhp.scss";

import { AppContext } from "../../contexts";

import { Icon } from "./icon";
import { generateUniqueHash } from "../../_helpers/utils";
import { Spinner } from "reactstrap";

let style = Object.assign({}, global);

const DocumentAssetField = ({
  maxSize = 5 * 1024 * 1024, // Default: 5MB
  maxCount = 5, // Default: maximum 5 files
  showNoFileMsg = false,
  showFileSize = false,
  showFileCountIndicator = false,
  updateFunction,
  defaultAssets = [],
  currentAssetList = [],
  deleteAssetFn,
  setCustomMsg,
}) => {
  const { updateFlashMessage } = useContext(AppContext);
  const fileInputRef = useRef(null);

  const [uploadedFiles, setUploadedFiles] = useState(defaultAssets);

  const uploadBrandAsset = (files, hashkey) => {
    if (updateFunction) updateFunction(files, hashkey);
  };

  const handleFileChange = event => {
    const files = Array.from(event.target.files);

    if (!files.length) return;

    // Validate file count
    const currentFileCount = uploadedFiles.length;
    const newFileCount = files.length;
    const totalCount = currentFileCount + newFileCount;

    if (totalCount > maxCount) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: `Maximum ${maxCount} files allowed.`,
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: `Maximum ${maxCount} files allowed.`,
        });
      }

      event.target.value = "";
      return;
    }

    // Validate each file
    const validFiles = [];
    const errors = [];

    let hashkey = `asset${generateUniqueHash()}`;
    files.forEach((file, index) => {
      // Check file type
      if (file.type !== "application/pdf") {
        errors.push(`${file.name}: Not a PDF file`);
        return;
      }

      // Check file size
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        const maxSizeInMB = (maxSize / (1024 * 1024)).toFixed(2);
        errors.push(`${file.name}: File size (${sizeInMB}MB) exceeds maximum allowed (${maxSizeInMB}MB)`);
        return;
      }

      validFiles.push(file);
    });

    // Show errors if any
    if (errors.length > 0) {
      if (setCustomMsg) {
        setCustomMsg({
          type: "error",
          message: errors.join("\n"),
        });
      } else {
        updateFlashMessage({
          type: "error",
          message: errors.join("\n"),
        });
      }

      event.target.value = "";
    }

    // Process valid files
    if (validFiles.length > 0) {
      // Convert files to a display format
      const newUploadedFiles = validFiles.map(file => ({
        // id: Date.now() + Math.random(),
        name: file.name,
        size: file.size,
        file: file,
        temp_id: hashkey,
      }));

      // Add to uploaded files state
      setUploadedFiles(prev => [...prev, ...newUploadedFiles]);

      // Here you can add your file upload logic
      uploadBrandAsset(files, hashkey);
    }
  };

  const handleDeleteFile = fileId => {
    deleteAssetFn(fileId);
  };

  const setDownloadingTag = (id, isDownloading = true) => {
    let updatedFiles = [...uploadedFiles];
    updatedFiles.map(item => {
      if (item.id == id) {
        if (isDownloading && !item.downloading) item.downloading = true;
        else if (!isDownloading && item.downloading) delete item.downloading;
      }
    });
    setUploadedFiles(updatedFiles);
  };

  const handleDownloadFile = async file => {
    if (!file.id) return;
    setDownloadingTag(file.id);

    let fileUrl = file.url;
    let fileName = file.name;
    try {
      const response = await fetch(fileUrl, {
        method: "GET",
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      setDownloadingTag(file.id, false);
      console.error(error);
      alert("Failed to download file");
    } finally {
      setDownloadingTag(file.id, false);
    }
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleAddClick = () => {
    if (uploadedFiles.length < maxCount && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setUploadedFiles([...currentAssetList]);
    }, 100);
  }, [currentAssetList]);

  return (
    <div>
      {/* Uploaded Files List */}
      <div className={style["complete-upload"]}>
        {uploadedFiles.length > 0 && (
          <ul>
            {uploadedFiles.map(file => (
              <li key={file.id}>
                <div className={cx(style["d-flex"], style["align-items-center"], style["w-75"])}>
                  <div className={style["icon-box"]}>
                    <Icon icon="ui-attach" />
                  </div>
                  <div className={cx(style["text-truncate"], style["mr-2"])}>
                    {file.name}
                    {showFileSize && (
                      <div className={style["text-muted"]} style={{ fontSize: "0.8rem" }}>
                        {formatFileSize(file.size)}
                      </div>
                    )}
                  </div>
                </div>
                <div className={cx(style["d-flex"], style["align-items-center"])}>
                  {(file.temp_id || file.downloading) && <Spinner size="sm" color="dark" className={style["mr-1"]} />}
                  {file.id && (
                    <>
                      {!file.downloading && (
                        <a onClick={() => handleDownloadFile(file)} style={{ cursor: "pointer" }}>
                          <Icon icon="ui-download2" additionalclass={"p-1 font-base"} />
                        </a>
                      )}
                      <a onClick={() => handleDeleteFile(file.id)} style={{ cursor: "pointer" }}>
                        <Icon icon="ui-trash" additionalclass={"p-1 font-base"} />
                      </a>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {uploadedFiles.length < 1 && showNoFileMsg && (
          <div className={style["text-muted"]} style={{ padding: "10px", textAlign: "center" }}>
            No files uploaded yet
          </div>
        )}
      </div>

      {/* File Upload Area */}
      {uploadedFiles.length < maxCount && (
        <div className={cx(style["fileUpload"], style["PDF_Upload"])}>
          <div className={cx(style["add-new"])} onClick={handleAddClick}>
            <Icon icon="ui-plus" />
          </div>
          Add PDF
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,application/pdf"
            title=""
            onChange={handleFileChange}
            disabled={uploadedFiles.length >= maxCount}></input>
        </div>
      )}

      {/* File count indicator */}
      {showFileCountIndicator && (
        <div className={style["text-muted"]} style={{ fontSize: "0.8rem", marginTop: "5px" }}>
          {uploadedFiles.length} of {maxCount} files uploaded
        </div>
      )}
    </div>
  );
};

export default DocumentAssetField;
